import Slider from 'rc-slider/lib/Slider';
import { HTMLProps, useContext, useEffect, useState } from 'react';
import CurrencyInput from 'react-currency-input-field';
import { Trans, useTranslation } from 'react-i18next';
import { useGetPensionCalcData } from '../../../../api/pension-robot/useGetPensionCalcData';
import { riskProfiles } from '../../../../config/constants';
import { PensionContext } from '../../../../contexts/pension-robot/PensionContext';
import { reverseFormatNumber, toPrice } from '../../../../utils/parsers';
import { Button } from '../../../general/Button';
import { SelectInput } from '../../../general/form-inputs/SelectInput';
import { ModalScreenPension } from "../../../general/ModalScreen";
import { OverlayHeader } from "../../expenses-overlay/OverlayHeader";
import { AccountType } from '../../../../contexts/pension-robot/PensionContext';
import { getResultSavingsAccount } from '../../../../utils/functions';
import { useGetInvestmentForecast } from '../../../../api/pension-robot/useGetInvestmentForecast';
import { DateTime } from 'luxon';
import { LoadingStateWrapper } from '../../../general/LoadingStateWrapper';

export const AdditionalPension: React.FC<{ setHiddenState: (hiddenState: boolean) => void } & HTMLProps<HTMLDivElement>> = ({ setHiddenState }) => {
    const {
        actions,
        parameters: { supplementInfo, personalInfo }
    } = useContext(PensionContext);
    const { t } = useTranslation('pension');
    const [yearSpaceModalShown, setYearSpaceModalShown] = useState(false);
    const [explanationsScreenShown, setExplanationScreenShown] = useState(false);
    const [riskProfileId, setRiskProfileId] = useState(riskProfiles.neutral);
    const [typeOfAdditionalPensionAccount, setTypeOfAdditionalPensionAccount] = useState('saving' as AccountType);
    const [monthlyPaymentAmount, setMonthlyPaymentAmount] = useState(0);
 
    const pensionCalcData = useGetPensionCalcData();
    const yearlyMarketRate = pensionCalcData.data?.marketRate ?? 0;
    // Get legalRetirementAge from api once implemented in backend
    //const legalRetirementAge = pensionCalcData.data?.legalRetirementAge ?? 0;
    const legalRetirementAge = 67;

    const riskProfilePickList = [
        {
            label: t('general-very-defensive'),
            value: riskProfiles.veryDefensive,
        },
        {
            label: t('general-defensive'),
            value: riskProfiles.defensive,
        },
        {
            label: t('general-neutral'),
            value: riskProfiles.neutral,
        },
        {
            label: t('general-offensive'),
            value: riskProfiles.offensive,
        },
        {
            label: t('general-very-offensive'),
            value: riskProfiles.veryOffensive
        },
    ];

    const investmentForecast = useGetInvestmentForecast(
        {
            riskProfileId: Number.parseInt(riskProfileId ?? '10') || 10,
            paymentSingleAmount: 0,
            paymentRecurringAmount: monthlyPaymentAmount,
            paymentRecurringInterval: 'Month',
            investmentEndDate: (DateTime.now().plus({ years: Math.max(legalRetirementAge - (personalInfo.self?.age ?? 0), 0) })).toISODate(),
            paymentRecurringEndDate: (DateTime.now().plus({ years: Math.max(legalRetirementAge - (personalInfo.self?.age ?? 0), 0) })).toISODate(),
            automaticRiskReduction: true,
            calculationMode: 'NewProduct'
        },
        false
    );

    const setAdditionalPension = () => {
        const computationResult = typeOfAdditionalPensionAccount === 'saving'
            ? getResultSavingsAccount(0, monthlyPaymentAmount * 100, 'month', personalInfo.self?.age ?? 0, legalRetirementAge, yearlyMarketRate)
            : investmentForecast.data?.forecastExpirationValues?.median
                ? investmentForecast.data?.forecastExpirationValues?.median * 100
                : 0;
        actions.setParameters({
            supplementInfo: {
                yearSpacePensionAccount: {
                    typeOfAdditionalPensionAccount: typeOfAdditionalPensionAccount,
                    riskProfileId: riskProfileId,
                    monthlyPaymentAmount: monthlyPaymentAmount,
                    predictedAmount: computationResult
                }
            }
        });
    };

    useEffect(() => {
        if (investmentForecast.data) {
            setAdditionalPension()
        }
    // TODO: setAdditionalPension should be wrapped in a useCallback, and then so does actions which
    // can have implications for the whole application. This should be taken up later
    }, [investmentForecast.data]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div>
            {supplementInfo?.yearSpace === undefined ? (
                <div className="shadow-lg-all-sides p-4 mx-4 bg-white rounded-lg">
                    <div className="font-bold mb-6 pr-14">
                        {t('pension-result-screen-additional-pension-title')}
                    </div>
                    <div className="mb-4 pr-14">
                        {t('pension-result-screen-additional-pension-subtitle1')}
                    </div>
                    <div className="mb-6 pr-14">
                        {t('pension-result-screen-additional-pension-subtitle2')}
                    </div>
                    <div className="flex flex-row">
                        <Button
                            tint="pink"
                            className="font-bold"
                            size="sm"
                            onClick={() => setYearSpaceModalShown(true)}
                        >
                            {t('pension-result-screen-additional-pension-calculate-button')}
                        </Button>
                        <div className="flex-1" />
                    </div>
                </div>
            ) : (!explanationsScreenShown ? (
                <div className="p-4 border-b">

                    <div className="rounded-lg bg-bnd-white-1 shadow-xl p-5 my-3 ">
                        <div className="mb-2 font-bold">
                            {t('pension-result-screen-additional-pension-title')}
                        </div>
                        <div className="mb-8">
                            <Trans
                                t={t}
                                i18nKey={"pension-result-screen-additional-pension-subtitle3"}
                                values={{
                                    maxYearSpace: toPrice((Math.floor(((supplementInfo?.yearSpace ?? 0) / 12) / 100) * 100), { onlyFullEuros: true })
                                }}
                            >
                                ...
                                <span className="font-bold">maxYearSpace</span>
                                ...
                            </Trans>
                        </div>
                        <span className="w-40 md:w-56 font-bold">{t('pension-result-screen-additional-pension-type-label')}</span>
                        <div className="flex flex-row items-center justify-start mb-5 mt-2">
                            <SelectInput
                                className="w-40 md:w-56"
                                onChange={(_value) =>
                                    setTypeOfAdditionalPensionAccount(_value as AccountType)
                                }
                                selected={typeOfAdditionalPensionAccount ?? 'saving'}
                                values={[
                                    {
                                        label: t('general-savings-account'),
                                        value: 'saving',
                                    },
                                    {
                                        label: t('general-investment-account'),
                                        value: 'investment',
                                    }
                                ]}
                            />
                        </div>
                        {typeOfAdditionalPensionAccount === 'investment' &&
                            <div>
                                <span className="w-40 md:w-56 font-bold">{t('pension-result-screen-additional-pension-risk-label')}</span>
                                <div className="flex flex-row items-center justify-start mb-5 mt-2">
                                    <SelectInput
                                        className="w-40 md:w-56"
                                        onChange={(_value) =>
                                            setRiskProfileId(_value)
                                        }
                                        selected={riskProfileId ?? '10'}
                                        values={riskProfilePickList}
                                    />
                                </div>
                            </div>
                        }
                        <span className="font-bold">{t('pension-result-screen-additional-pension-monthly-amount-label')}</span>
                        <div className="flex flex-row items-center justify-start mb-5 mt-2">
                            <CurrencyInput
                                className={`w-28 md:w-28 py-2 px-4 mr-8 border border-gray-200 rounded-lg focus:outline-none`}
                                value={monthlyPaymentAmount}
                                type="text"
                                fixedDecimalLength={0}
                                decimalsLimit={0}
                                placeholder="Periodieke inleg"
                                intlConfig={{ currency: 'EUR', locale: 'nl-NL' }}
                                onValueChange={(_value) => {
                                    setMonthlyPaymentAmount(_value ? reverseFormatNumber(_value, 'nl-NL') : 0)
                                }}
                            />
                            <Slider
                                className={"lg:w-36 sm:w-16"}
                                max={(supplementInfo.yearSpace / 100) / 12}
                                min={0}
                                step={1}
                                value={monthlyPaymentAmount}
                                onChange={(_value) => {
                                    setMonthlyPaymentAmount(_value)
                                }}
                            />
                        </div>
                        <LoadingStateWrapper
                            isLoading={investmentForecast.isLoading}
                            isError={investmentForecast.isError}
                            isIdle={false}
                            isFetching={investmentForecast.isFetching}
                        >
                            <div className="flex flex-row flex-wrap gap-2 sm:gap-4">

                                <Button
                                    tint="green"
                                    className="font-bold w-48"
                                    size="sm"
                                    onClick={() => {
                                        if (typeOfAdditionalPensionAccount === 'investment') {
                                            investmentForecast.refetch().then((value) => {
                                                setHiddenState(true);
                                            })
                                        }
                                        else {
                                            setAdditionalPension()
                                            setHiddenState(true)
                                        }
                                    }}
                                >
                                    {t('pension-cocktail-fill-screen-results-save-or-invest-btn-add')}
                                </Button>
                                <Button
                                    tint="neutral-no-hover"
                                    className="font-bold w-48"
                                    size="sm"
                                    onClick={() => setExplanationScreenShown(true)}
                                >
                                    {t('general-cancel')}
                                </Button>
                                <div className="flex-1" />
                            </div>
                        </LoadingStateWrapper>
                    </div>
                </div>
            ) : (
                <div className="p-4 border-b">
                    <div className="mb-2">
                        {t('pension-result-screen-additional-pension-explanation1')}
                    </div>
                    <div className="mb-8">
                        <Trans
                            t={t}
                            i18nKey={"pension-result-screen-additional-pension-explanation2"}
                            values={{
                                maxYearSpace: toPrice((Math.floor((supplementInfo?.yearSpace ?? 0) / 100) * 100), { onlyFullEuros: true }),
                                maxYearSpaceMonth: toPrice((Math.floor(((supplementInfo?.yearSpace ?? 0) / 12) / 100) * 100), { onlyFullEuros: true })
                            }}
                        >
                        </Trans>
                    </div>

                    <div className="rounded-lg bg-bnd-white-1 shadow-xl p-5 my-3 ">
                        <span className="font-bold">{t('pension-result-screen-additional-pension-monthly-amount-label')}</span>
                        <div className="flex flex-row items-center justify-start mb-5 mt-2">
                            <CurrencyInput
                                className={`w-28 md:w-28 py-2 px-4 mr-8 border border-gray-200 rounded-lg focus:outline-none`}
                                value={monthlyPaymentAmount}
                                type="text"
                                fixedDecimalLength={0}
                                decimalsLimit={0}
                                placeholder="Periodieke inleg"
                                intlConfig={{ currency: 'EUR', locale: 'nl-NL' }}
                                onValueChange={(_value) => {
                                    setMonthlyPaymentAmount(_value ? reverseFormatNumber(_value, 'nl-NL') : 0)
                                }}
                            />
                            <Slider
                                className={"lg:w-36 sm:w-16"}
                                max={(supplementInfo.yearSpace / 100) / 12}
                                min={0}
                                step={1}
                                value={monthlyPaymentAmount}
                                onChange={(_value) => {
                                    setMonthlyPaymentAmount(_value)
                                }}
                            />
                        </div>
                        <div className="flex flex-row mb-5">
                            <Button
                                tint="green"
                                className="font-bold w-48"
                                size="sm"
                                onClick={() => setExplanationScreenShown(false)}
                            >
                                {t('pension-cocktail-fill-screen-results-save-or-invest-btn-add')}
                            </Button>

                            <div className="flex-1" />
                        </div>
                        <div className="flex flex-row">
                            <button
                                className="text-gray-800 underline"
                                onClick={() => {
                                    setYearSpaceModalShown(true);
                                }}
                            >
                                {t('pension-result-screen-additional-pension-recalculate-button')}
                            </button>
                            <div className="flex-1" />
                        </div>
                    </div>
                </div>

            ))}
            <YearSpaceModal
                isShown={yearSpaceModalShown}
                onClose={() => { setYearSpaceModalShown(false); setExplanationScreenShown(true) }}
                setMonthlyPaymentAmount={setMonthlyPaymentAmount} />
        </div>
    );
};

const YearSpaceModal: React.FC<{ isShown: boolean; onClose: () => void; setMonthlyPaymentAmount: (monthlyPaymentAmount: number) => void }> = ({ isShown, onClose, setMonthlyPaymentAmount }) => {
    const { t } = useTranslation('pension');
    const {
        parameters: { supplementInfo, personalInfo },
        actions,
    } = useContext(PensionContext);
    const pensionCalcDataQuery = useGetPensionCalcData();

    const calculateYearSpace = () => {
        const aowFranchise = pensionCalcDataQuery.data?.aowFranchise ?? 0;
        const maximumPensionFoundation = pensionCalcDataQuery.data?.maximumPensionFoundation ?? 0;
        const premiumPercentagePensionFoundation = pensionCalcDataQuery.data?.premiumPercentagePensionFoundation ?? 0;
        const pensionGrowthFactor = pensionCalcDataQuery.data?.pensionGrowthFactor ?? 0;

        const factorA = (supplementInfo?.yearSpaceParams?.factorA ?? 0) / 100;
        const FORIncrease = (supplementInfo?.yearSpaceParams?.FORIncrease ?? 0) / 100;
        const FORDecrease = (supplementInfo?.yearSpaceParams?.FORDecrease ?? 0) / 100;
        const grossYearlyIncome = (supplementInfo?.yearSpaceParams?.grossYearlyIncome ?? 0) / 100;

        const FOR = FORIncrease - FORDecrease;

        const res = Math.max(0,
            Math.ceil(
                premiumPercentagePensionFoundation
                * ((grossYearlyIncome < maximumPensionFoundation)
                    ? (grossYearlyIncome - aowFranchise)
                    : (maximumPensionFoundation - aowFranchise))
                - (pensionGrowthFactor * factorA)
                - FOR
            ) * 100
        );

        actions.setParameters({ supplementInfo: { yearSpace: res } });
        setMonthlyPaymentAmount(Math.floor((res / 12) / 100));
    }

    return (
        <ModalScreenPension isShown={isShown}>
            <div className="bg-white md:mx-auto md:rounded-xl p-8">
                <OverlayHeader
                    title={t('pension-result-screen-additional-pension-yearspace-modal-title')}
                    onClose={onClose}
                />

                <div className="flex flex-col gap-4 mt-8" >
                    <div className="flex flex-row flex-wrap gap-2 sm:gap-4 items-center justify-start">
                        <span className="w-40 md:w-80">{t('pension-result-screen-additional-pension-yearspace-modal-factora-label')}</span>
                        <CurrencyInput
                            type="text"
                            className={`w-40 md:w-56 py-2 px-4 border border-gray-200 rounded-lg focus:outline-none`}
                            fixedDecimalLength={0}
                            decimalsLimit={2}
                            intlConfig={{ currency: 'EUR', locale: 'nl-NL' }}
                            allowDecimals={false}
                            value={(supplementInfo?.yearSpaceParams?.factorA ?? 0) / 100}
                            onValueChange={(value = '') => {
                                actions.setParameters({ supplementInfo: { yearSpaceParams: { factorA: reverseFormatNumber(value, 'nl-NL') * 100 } } });
                            }}
                        />
                    </div>
                    {personalInfo.self?.employmentStatus === 'self-employed' &&
                        <div className="flex flex-row flex-wrap gap-2 sm:gap-4 items-center justify-start">
                            <span className="w-40 md:w-80">{t('pension-result-screen-additional-pension-yearspace-modal-forincrease-label')}</span>
                            <CurrencyInput
                                type="text"
                                className={`w-40 md:w-56 py-2 px-4 border border-gray-200 rounded-lg focus:outline-none`}
                                fixedDecimalLength={0}
                                decimalsLimit={2}
                                intlConfig={{ currency: 'EUR', locale: 'nl-NL' }}
                                allowDecimals={false}
                                value={(supplementInfo?.yearSpaceParams?.FORIncrease ?? 0) / 100}
                                onValueChange={(value = '') => {
                                    actions.setParameters({ supplementInfo: { yearSpaceParams: { FORIncrease: reverseFormatNumber(value, 'nl-NL') * 100 } } });
                                }}
                            />
                        </div>
                    }
                    {personalInfo.self?.employmentStatus === 'self-employed' &&
                        <div className="flex flex-row flex-wrap gap-2 sm:gap-4 items-center justify-start">
                            <span className="w-40 md:w-80">{t('pension-result-screen-additional-pension-yearspace-modal-fordecrease-label')}</span>
                            <CurrencyInput
                                type="text"
                                className={`w-40 md:w-56 py-2 px-4 border border-gray-200 rounded-lg focus:outline-none`}
                                fixedDecimalLength={0}
                                decimalsLimit={2}
                                intlConfig={{ currency: 'EUR', locale: 'nl-NL' }}
                                allowDecimals={false}
                                value={(supplementInfo?.yearSpaceParams?.FORDecrease ?? 0) / 100}
                                onValueChange={(value = '') => {
                                    actions.setParameters({ supplementInfo: { yearSpaceParams: { FORDecrease: reverseFormatNumber(value, 'nl-NL') * 100 } } });
                                }}
                            />
                        </div>
                    }
                    <div className="flex flex-row flex-wrap gap-2 sm:gap-4 items-center justify-start">
                        <span className="w-40 md:w-80">{t('pension-result-screen-additional-pension-yearspace-modal-income-label')}</span>
                        <CurrencyInput
                            type="text"
                            className={`w-40 md:w-56 py-2 px-4 border border-gray-200 rounded-lg focus:outline-none`}
                            fixedDecimalLength={0}
                            decimalsLimit={2}
                            intlConfig={{ currency: 'EUR', locale: 'nl-NL' }}
                            allowDecimals={false}
                            value={(supplementInfo?.yearSpaceParams?.grossYearlyIncome ?? 0) / 100}
                            onValueChange={(value = '') => {
                                actions.setParameters({ supplementInfo: { yearSpaceParams: { grossYearlyIncome: reverseFormatNumber(value, 'nl-NL') * 100 } } });
                            }}
                        />
                    </div>

                    <div className="flex gap-2 sm:gap-4 flex-row flex-wrap justify-start mt-8">
                        <Button
                            tint="green"
                            size="sm"
                            onClick={() => {
                                calculateYearSpace();
                                onClose();
                            }}
                            className="w-30 sm:w-44 h-10"
                        >
                            {t('general-calculate')}
                        </Button>
                        <div style={{ flex: 6 }}></div>
                    </div>
                </div>
            </div>
        </ModalScreenPension>
    )
};
